import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { setApiResponse, setEditApiResponse } from '../../redux/FlatFile/FlatFileStateActions';
import { useLocation } from 'react-router-dom';
import { isLoggedProps } from '../../redux/UserInfo/IsLoggedInterfaces';
import './FlatFile.css';
import { FlatFileUploadProps } from './FlatFileInterfaces';
import MenuBar from '../DataViewer/MenuBar/MenuBar';

import { PopUpSpinner } from '../CommonFunctions/DialogComponents';
import { SheetDialog } from './SheetDialog';
import { FileTypeSelector, UploadArea, ErrorDialog } from './FlatFileComponents';

import {
  getMaxFileSizeLimit,
  validateFileSelection,
  validateJsonContent,
  getExcelSheetNames,
  uploadFile,
} from './FlatFileUploadHelpers';
import { Helmet } from 'react-helmet-async';

const FlatFileUpload = ({ token, setApiResponse, setEditApiResponse }: FlatFileUploadProps) => {
  const navigate = useNavigate();
  const { parentId } = useParams();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [selectedFileType, setSelectedFileType] = useState<string | undefined>();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sheetChooseDialogShow, setSheetChooseDialogShow] = useState(false);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState({ fileId: '', fileName: '' });
  const [sheetError, setSheetError] = useState({ isError: false, message: '' });

  const location = useLocation();
  const state = location.state;

  // Get max file size based on user access level
  const MAX_FILE_SIZE_MB = getMaxFileSizeLimit(token);

  const setDataToEditApiResponse = useCallback(
    (data: any) => {
      const fileObj = {
        fileId: data.fileId,
        name: data.name,
        dateFormat: data.dateFormat,
        timestampFormat: data.timestampFormat,
        columnInfos: data.columnInfos,
        sampleRecords: data.sampleRecords,
        fileType: selectedFileType,
      };
      setEditApiResponse(fileObj);
    },
    [selectedFileType, setEditApiResponse],
  );

  // Function to handle file type selection
  const handleFileType = (fileType: string) => {
    setSelectedFileType(fileType);
    setSelectedFile(undefined);
  };

  // Function to validate and handle file selection
  const handleFileSelect = (file: File | undefined) => {
    if (!file) {
      setSelectedFile(undefined);
      return;
    }

    const validation = validateFileSelection(file, selectedFileType, MAX_FILE_SIZE_MB);
    if (!validation.isValid) {
      if (validation.errorMessage) {
        setErrorDialogMessage(validation.errorMessage);
        setErrorDialogOpen(true);
      }
      return;
    }

    setSelectedFile(file);
  };

  // Function to handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedFile) {
      setErrorDialogMessage('Please select a file to upload');
      setErrorDialogOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    // Handle Excel files differently to get sheet names
    if (selectedFileType === 'excel') {
      setSheetLoading(true);
      setSheetChooseDialogShow(true);

      const result = await getExcelSheetNames(formData, token);

      setSheetLoading(false);

      if (result.success && result.sheetNames && result.fileId && result.fileName) {
        setSheetNames(result.sheetNames);
        setFileInfo({
          fileId: result.fileId,
          fileName: result.fileName,
        });
      } else {
        setSheetError({
          isError: true,
          message: result.errorMessage ?? 'Failed to retrieve sheet names',
        });
      }
      return;
    }

    // For CSV and JSON files
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const fileContent = e.target.result;

      // Validate JSON format
      if (selectedFileType === 'json') {
        const validation = validateJsonContent(fileContent);
        console.log(validation.isValid);
        if (!validation.isValid) {
          setErrorDialogMessage(validation.errorMessage || 'Invalid JSON format');
          setErrorDialogOpen(true);
          setSelectedFile(undefined);
          return;
        }
      }

      setLoading(true);

      const result = await uploadFile(formData, selectedFileType!, token);
      setLoading(false);
      if (result.success && result.data) {
        if (state?.mode === 'update') {
          navigate(`/editflatfile/${parentId}`, {
            state: {
              mode: 'update',
              parentId,
              fileId: state?.fileId,
              file: { ...result.data, fileType: selectedFileType },
            },
          });
        } else {
          setApiResponse(result.data);
          setDataToEditApiResponse(result.data);
          navigate(`/editflatfile/${parentId}`, {
            state: { mode: 'New', parentId },
          });
        }
      } else {
        setErrorDialogMessage(result.errorMessage || 'Upload failed');
        setErrorDialogOpen(true);
        setSelectedFile(undefined);
      }
    };

    reader.readAsText(selectedFile);
  };

  const disableEdit = false;

  return (
    <>
      <Helmet prioritizeSeoTags defer>
        <title>Flat File Upload | Silzila</title>
      </Helmet>
      <div>
        <MenuBar from="fileUpload" disableEdit={disableEdit} />
        <SheetDialog
          sheetChooseDialogShow={sheetChooseDialogShow}
          loading={loading}
          token={token}
          fileInfo={fileInfo}
          sheetLoading={sheetLoading}
          sheetError={sheetError}
          sheetNames={sheetNames}
          parentId={parentId}
          setLoading={setLoading}
          setSheetChooseDialogShow={setSheetChooseDialogShow}
          setErrorDialogOpen={setErrorDialogOpen}
          setErrorDialogMessage={setErrorDialogMessage}
          setDataToEditApiResponse={setDataToEditApiResponse}
          setSheetError={setSheetError}
          setSelectedFile={setSelectedFile}
        />

        <div style={{ display: 'flex' }}>
          <FileTypeSelector selectedFileType={selectedFileType} onFileTypeSelect={handleFileType} />
          <UploadArea
            selectedFileType={selectedFileType}
            selectedFile={selectedFile}
            handleFileSelect={handleFileSelect}
            handleSubmit={handleSubmit}
            mode={state?.mode === 'update'}
          />
        </div>
        <ErrorDialog
          open={errorDialogOpen}
          message={errorDialogMessage}
          onClose={() => setErrorDialogOpen(false)}
        />

        <PopUpSpinner show={loading} />
      </div>
    </>
  );
};

const mapStateToProps = (state: isLoggedProps) => ({
  token: state.isLogged.accessToken,
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  setApiResponse: (file: any) => dispatch(setApiResponse(file)),
  setEditApiResponse: (file: any) => dispatch(setEditApiResponse(file)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FlatFileUpload);

// import {
//   Button,
//   Dialog,
//   DialogActions,
//   DialogContentText,
//   DialogTitle,
// } from "@mui/material";
// import React, { useCallback, useState } from "react";
// import { connect } from "react-redux";
// import { useNavigate,  useParams } from "react-router-dom";

// import { Dispatch } from "redux";
// import {
//   setApiResponse,
//   setEditApiResponse,
// } from "../../redux/FlatFile/FlatFileStateActions";
// import { isLoggedProps } from "../../redux/UserInfo/IsLoggedInterfaces";
// import FetchData from "../ServerCall/FetchData";
// import "./FlatFile.css";
// import FileDropZone from "./FileDropZone";
// import { FlatFileUploadProps } from "./FlatFileInterfaces";
// import Logger from "../../Logger";
// import MenuBar from "../DataViewer/MenuBar";
// import jsonFileIcon from "../../assets/jsonFileIcon.png";
// import csvFileIcon from "../../assets/csvFileIcon.png";
// import xlsxFileIcon from "../../assets/xlsxFileIcon.png";
// import { jwtDecode } from "jwt-decode";
// import { fontSize, palette } from "../..";
// import { PopUpSpinner } from "../CommonFunctions/DialogComponents";
// import HoverButton from "../Buttons/HoverButton";
// import { ffButtonStyle } from "./muiStyles";
// import { SheetDialog } from "./SheetDialog";

// const FlatFileUpload = ({
//   token,
//   setApiResponse,
//   setEditApiResponse,
// }: FlatFileUploadProps) => {
//   const navigate = useNavigate();
//   const [selectedFile, setSelectedFile] = useState<File | undefined>();
//   const [selectedFileType, setSelectedFileType] = useState<string | undefined>(); // Filetype state
//   // const [selectedSheetName, setSelectedSheetName] = useState<string>(""); // Sheetname state
//   const { parentId } = useParams();

//   // Error dialog state
//   const [errorDialogOpen, setErrorDialogOpen] = useState(false);
//   const [errorDialogMessage, setErrorDialogMessage] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [sheetChooseDialogShow, setSheetChooseDialogShow] = useState(false);
//   const [sheetNames, setSheetNames] = useState<string[]>([]);
//   const [sheetLoading, setSheetLoading] = useState(false);
//   const [fileInfo, setFileInfo] = useState<{fileId:string,fileName:string,}>({
//     fileId: "",
//     fileName: "",
//   });
//   const [sheetError, setSheetError] = useState<{
//     isError: boolean;
//     message: string;
//   }>({ isError: false, message: "" });
//   type TExcelDetails = {
//     fileId: string;
//     fileName: string;
//     sheetNames: string[];
//   };
//   const setDataToEditApiResponse = useCallback(
//     (data: any) => {
//       const fileObj = {
//         fileId: data.fileId,
//         name: data.name,
//         dateFormat: data.dateFormat,
//         timestampFormat: data.timestampFormat,
//         columnInfos: data.columnInfos,
//         sampleRecords: data.sampleRecords,
//         fileType: selectedFileType,
//       };
//       setEditApiResponse(fileObj);
//     },
//     [selectedFileType, setEditApiResponse]
//   );

//   // Function to handle file type selection
//   const handleFileType = (fileType: string) => {
//     setSelectedFileType(fileType);
//     setSelectedFile(undefined); // reset selectedFile when file type change
//     // setSelectedSheetName(""); // reset sheet name when file type change
//     // setSelectedSheet(""); // reset sheet name when file type change
//   };
//   // const handleSheetChange = useCallback((newSheet:string) => {
//   //   setSelectedSheet(newSheet);
//   // }, []);
//   // const handleDialogClose = useCallback((val:boolean) => {
//   //   setSheetChooseDialogShow(val);
//   // }, []);
//   const decodedToken: any = jwtDecode(token);
//   const access = decodedToken.access;

//   const MAX_FILE_SIZE_MB = access === "community" ? 50 : 100; // File size limit /*Business User limit -100MB Community User - 50MB*/

//   // function to handle file select
//   const handleFileSelect = (file: File | undefined) => {
//     if (file) {
//       const fileExtension = file.name.split(".").pop()?.toLowerCase();
//       if (
//         (selectedFileType === "csv" && fileExtension !== "csv") ||
//         (selectedFileType === "json" && fileExtension !== "json")
//       ) {
//         setErrorDialogMessage(
//           `Invalid file type. Expected a ${selectedFileType.toUpperCase()} file.`
//         );
//         setErrorDialogOpen(true);
//         setSelectedFile(undefined);
//         return;
//       }
//       if (selectedFileType === "excel" && fileExtension !== "xlsx") {
//         setErrorDialogMessage(
//           "Please upload Excel file (.xlsx format) only. Other file types are not supported."
//         );
//         setErrorDialogOpen(true);
//         setSelectedFile(undefined); // Reset selected file
//         // setSelectedSheetName(""); // Reset sheet name
//         return;
//       }

//       const maxFileSize = MAX_FILE_SIZE_MB * 1024 * 1024;
//       if (file.size > maxFileSize) {
//         setErrorDialogMessage(`File size exceeds the maximum limit of ${MAX_FILE_SIZE_MB} MB.`);
//         setErrorDialogOpen(true);
//         setSelectedFile(undefined);
//         // setSelectedSheetName("");
//         return;
//       }
//     }
//     setSelectedFile(file);
//   };

//   // Function to handle form submission
//   const handleSubmit = async (event: any) => {
//     event.preventDefault();

//     if (!selectedFile) {
//       setErrorDialogMessage("Please select a file to upload");
//       setErrorDialogOpen(true);
//       return;
//     }

//     const formData = new FormData();
//     formData.append("file", selectedFile);
//     if (selectedFileType === "excel") {
//       setSheetLoading(true);
//       setSheetChooseDialogShow(true);
//       const res = await FetchData({
//         requestType: "withData",
//         method: "POST",
//         url: "file-upload-excel-sheetname",
//         data: formData,
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "multipart/form-data",
//         },
//       });
//       setSheetLoading(false);
//       console.log(res);
//       if (res.status) {
//         const _res: TExcelDetails = res.data;
//         setSheetNames(_res.sheetNames);
//         setFileInfo({fileId:_res.fileId,fileName:_res.fileName});
//       } else {
//         setSheetError({
//           isError: true,
//           message: res.data ?? "An error Occured",
//         });
//       }

//       return;
//     }
//     const reader = new FileReader();

//     reader.onload = async (e: any) => {
//       const fileContent = e.target.result;
//       if (selectedFileType === "excel") return;
//       else {
//         if (selectedFileType === "json") {
//           try {
//             JSON.parse(fileContent);
//           } catch {
//             setErrorDialogMessage("JSON Object is not in the expected Format");
//             setErrorDialogOpen(true);
//             setSelectedFile(undefined); // reset selectedFile when error occurs
//             return;
//           }
//         }

//         setLoading(true);
//         var result: any = await FetchData({
//           requestType: "withData",
//           method: "POST",
//           url: `file-upload?filetype=${selectedFileType}`,
//           data: formData,
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "multipart/form-data",
//           },
//         });

//         if (result.status) {
//           setApiResponse(result.data);
//           setDataToEditApiResponse(result.data);
//           setLoading(false);
//           navigate(`/editflatfile/${parentId}`, {
//             state: { mode: "New", parentId: parentId },
//           });
//         } else {
//           setLoading(false);
//           Logger("info", "error");
//           const errorMessage = result.data?.message ?? "Error uploading the file";
//           setErrorDialogMessage(errorMessage);
//           setErrorDialogOpen(true);
//           setSelectedFile(undefined);
//         }
//       }
//     };

//     // if (selectedFileType === "excel") {
//     //   reader.readAsArrayBuffer(selectedFile);
//     // } else {
//     // }
//     reader.readAsText(selectedFile);
//   };

//   return (
//     <div>
//       <MenuBar from="fileUpload" />
//       <SheetDialog
//         sheetChooseDialogShow={sheetChooseDialogShow}
//         loading={loading}
//         token={token}
//         fileInfo={fileInfo}
//         sheetLoading={sheetLoading}
//         sheetError={sheetError}
//         sheetNames={sheetNames}
//         parentId={parentId}
//         setLoading={setLoading}
//         setSheetChooseDialogShow={setSheetChooseDialogShow}
//         setErrorDialogOpen={setErrorDialogOpen}
//         setErrorDialogMessage={setErrorDialogMessage}
//         setDataToEditApiResponse={setDataToEditApiResponse}
//         setSheetError={setSheetError}
//         setSelectedFile={setSelectedFile}

//       // selectedSheetName={selectedSheetName}
//       // setSelectedSheetName={setSelectedSheetName}
//       />
//       <div style={{ display: "flex" }}>
//         <div
//           className="rightsidebar-container"
//           style={{
//             borderRight: "2px solid rgb(224, 224, 224)",
//             height: "100vh",
//           }}
//         >
//           <div>
//             <div>
//               <h2
//                 className="header"
//                 style={{
//                   fontSize: fontSize.large,
//                   color: palette.primary.contrastText,
//                   fontWeight: "bold",
//                 }}
//               >
//                 Select a File Type
//               </h2>
//             </div>
//             <div className="icon-container">
//               <div
//                 className={`csv-container ${
//                   selectedFileType === "csv" ? "selected-container" : ""
//                 }`}
//                 style={{ display: "flex" }}
//                 onClick={() => handleFileType("csv")}
//               >
//                 <div className="csv-icon">
//                   <img src={csvFileIcon} className="csv-img" alt="CSV" />
//                 </div>
//                 <div>
//                   <p
//                     style={{
//                       fontSize: fontSize.medium,
//                       color: palette.primary.contrastText,
//                     }}
//                   >
//                     CSV
//                   </p>
//                 </div>
//               </div>

//               <div
//                 className={`json-container ${
//                   selectedFileType === "json" ? "selected-container" : ""
//                 }`}
//                 style={{ display: "flex" }}
//                 onClick={() => handleFileType("json")}
//               >
//                 <div className="json-icon">
//                   <img src={jsonFileIcon} className="json-img" alt="JSON" />
//                 </div>
//                 <div>
//                   <p
//                     style={{
//                       fontSize: fontSize.medium,
//                       color: palette.primary.contrastText,
//                     }}
//                   >
//                     JSON
//                   </p>
//                 </div>
//               </div>

//               <div
//                 className={`excel-container ${
//                   selectedFileType === "excel" ? "selected-container" : ""
//                 }`}
//                 style={{ display: "flex" }}
//                 onClick={() => handleFileType("excel")}
//               >
//                 <div className="excel-icon">
//                   <img src={xlsxFileIcon} className="excel-img" alt="Excel" />
//                 </div>
//                 <div>
//                   <p
//                     style={{
//                       fontSize: fontSize.medium,
//                       color: palette.primary.contrastText,
//                     }}
//                   >
//                     EXCEL
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {selectedFileType ? (
//           <div className="FileUploadContainer">
//             <div
//               className="uploadFileTitle"
//               style={{
//                 fontSize: fontSize.large,
//                 color: palette.primary.contrastText,
//                 fontWeight: "bold",
//               }}
//             >
//               Upload {selectedFileType.toUpperCase()} File
//             </div>
//             <FileDropZone
//               setSelectedFile={handleFileSelect}
//               selectedFile={selectedFile}
//               fileType={selectedFileType}
//             />
//             {/* <div style={{ height: "60px" }}>
//               {selectedFileType === "excel" && (
//                 <div className="excelContainer">
//                   <label className="sheetNameLabel" style={{fontSize:fontSize.medium,color:palette.primary.contrastText}}>ENTER SHEET NAME:</label>
//                   <input
//                     className="sheetNameInput"
//                     placeholder="Sheet Name"
//                     type="text"
//                     value={selectedSheetName}
//                     style={{fontSize:fontSize.medium,color:palette.primary.contrastText}}
//                     onChange={(e) => setSelectedSheetName(e.target.value)}
//                   />
//                 </div>
//               )}
//             </div> */}
//             <div className="file-upload-button-container">
//               {/* <Button
//                 style={{
//                   textTransform: "none",
//                   color: "#2bb9bb",
//                   border: "2px solid  #2bb9bb",
//                   padding: "5px 20px",
//                   borderRadius: "5px",
//                   height: "40px",
//                   lineHeight:'normal',
//                   fontSize:fontSize.medium,
//                 }}
//                 onClick={handleSubmit}
//               >
//                 Upload
//               </Button> */}
//               <HoverButton
//                 onClick={handleSubmit}
//                 text="Upload"
//                 color="primary.main"
//                 hoverColor="secondary.contrastText"
//                 backgroundColor="secondary.contrastText"
//                 hoverBackgroundColor="primary.main"
//                 transitionTime="0.2s"
//                 sx={{
//                   ...ffButtonStyle,
//                   height: "2.5rem",
//                   border: `1px solid ${palette.primary.main}`,
//                   fontSize: fontSize.medium,
//                   lineHeight: "normal",
//                 }}
//               />
//             </div>
//           </div>
//         ) : (
//           <div className="select-container">
//             <p
//               style={{
//                 fontSize: fontSize.medium,
//                 color: palette.primary.contrastText,
//                 fontWeight: "bold",
//               }}
//             >
//               Select a File Type
//             </p>
//           </div>
//         )}
//       </div>

//       <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
//         <div className="dialog-container">
//           <DialogTitle className="dialog-title">ERROR!</DialogTitle>
//           <DialogContentText
//             className="dialog-content-text"
//             sx={{
//               display: "flex",
//               textAlign: "center",
//               justifyContent: "center",
//               alignContent: "center",
//               paddingLeft: " 20px",
//               paddingRight: "20px",
//               paddingBottom: "5px",
//             }}
//           >
//             {errorDialogMessage}
//           </DialogContentText>
//           <DialogActions className="dialog-actions">
//             <Button
//               onClick={() => {
//                 setErrorDialogOpen(false);
//                 //  setSelectedFile(undefined);
//               }}
//               className="dialog-button"
//               sx={{
//                 color: "gray",
//                 border: "1px solid gray",
//                 marginBottom: "10px",
//                 marginRight: "15px",
//                 "&:hover": {
//                   backgroundColor: " #8c6bb1",
//                   color: "white",
//                 },
//               }}
//             >
//               OK
//             </Button>
//           </DialogActions>
//         </div>
//       </Dialog>
//       <PopUpSpinner
//         show={loading}
//         // paperProps={{ style: { backgroundColor: "red", boxShadow: "none" } }}
//         // sx={{ marginLeft: "16.7rem" }}
//       />
//     </div>
//   );
// };

// const mapStateToProps = (state: isLoggedProps, ownProps: any) => {
//   return {
//     token: state.isLogged.accessToken,
//   };
// };

// const mapDispatchToProps = (dispatch: Dispatch<any>) => {
//   return {
//     setApiResponse: (file: any) => dispatch(setApiResponse(file)),
//     setEditApiResponse: (file: any) => dispatch(setEditApiResponse(file)),
//   };
// };

// export default connect(mapStateToProps, mapDispatchToProps)(FlatFileUpload);
